import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { WorkOrderNote, NoteCategory } from '@/types';

export function useBookingNotes(bookingId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workOrders.notes(bookingId ?? ''),
    queryFn: () => api.get<WorkOrderNote[]>(`/work-orders/${bookingId}/notes`),
    enabled: !!bookingId,
  });
}

export function useAddBookingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      category,
      content,
    }: {
      bookingId: string;
      category: NoteCategory;
      content: string;
    }) =>
      api.post<WorkOrderNote>(`/work-orders/${bookingId}/notes`, {
        category,
        content,
      }),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.notes(bookingId),
      });
    },
  });
}

export function useUpdateBookingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      noteId,
      content,
    }: {
      bookingId: string;
      noteId: string;
      content: string;
    }) =>
      api.patch<WorkOrderNote>(`/work-orders/${bookingId}/notes/${noteId}`, {
        content,
      }),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.notes(bookingId),
      });
    },
  });
}

export function useDeleteBookingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      noteId,
    }: {
      bookingId: string;
      noteId: string;
    }) => api.delete(`/work-orders/${bookingId}/notes/${noteId}`),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.notes(bookingId),
      });
    },
  });
}

export function usePinBookingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      noteId,
      pinned,
    }: {
      bookingId: string;
      noteId: string;
      pinned: boolean;
    }) =>
      api.patch<WorkOrderNote>(`/work-orders/${bookingId}/notes/${noteId}/pin`, {
        pinned,
      }),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.notes(bookingId),
      });
    },
  });
}
